import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import mergeWith from 'lodash.mergewith';
import AppContext from '../../common/contexts/AppContext';
import { getLanguage } from '../../common/contexts/LanguageContext';
import {
    prepareResultsClassData,
    prepareBurstClassData,
} from '../../common/utils/prepareClassData';
import { BURST, DIBELS8, READING, IDEL2, CLPL } from '../../constants/applications';
import {
    applicationsType,
    classeStudentsType,
    staffType,
    formsType,
    appGroupType,
    bmWindowType,
    gatingRulesConfigType,
} from '../../constants/prop-types';
import {
    createFormsMap,
    createBurstFormsMap,
    createCLPLFormsMap,
} from '../../common/utils/prepareFormsData';
import { getForms, getResults, getCutscores, getGoals, getZoG } from '../../common/storage/storage';
import useLoading from '../../common/hooks/useLoading';
import Loading from '../loading/Loading';
import { useFeatureToggleContext } from '../../common/contexts/FeatureToggleContext';

const DashboardDataProvider = ({
    staff,
    applications,
    classesStudents,
    forms: formsFromProps,
    reloadPage,
    gatingRulesConfig,
    checkAndGetSessionStatus,
    isUnsyncedData,
    isDemoClassSelected,
    applicationSpecificCallbacks,
    selectedClassId,
    selectedAppGroup,
    bmWindowConfig,
    appBMWindows,
    currentBMWindow,
    nextBMWindow,
    selectedAssessmentType,
    fourthPeriodAvailability,
    children,
}) => {
    const fetchForms = useCallback(() => getForms(applications), [applications]);
    const fetchCutscores = useCallback(() => getCutscores(applications), [applications]);
    const fetchGoals = useCallback(() => getGoals(applications), [applications]);
    const fetchZoG = useCallback(() => getZoG(applications), [applications]);

    const { isGatingRulesRegulationEnabled } = useFeatureToggleContext();

    const [formsFromStore, isFormsLoadingInProgress] = useLoading(fetchForms, {});

    const forms = useMemo(() => ({ ...formsFromProps, ...formsFromStore }), [
        formsFromProps,
        formsFromStore,
    ]);

    const formsMapBurst = useMemo(() => createBurstFormsMap(forms[BURST]), [forms]);
    const formsMapD8 = useMemo(() => createFormsMap(forms[DIBELS8]), [forms]);
    const formsMapIdel2 = useMemo(() => createFormsMap(forms[IDEL2]), [forms]);
    const formsMapCLPL = useMemo(() => createCLPLFormsMap(forms[CLPL]), [forms]);

    const formsMap = { ...formsMapBurst, ...formsMapD8, ...formsMapIdel2, ...formsMapCLPL };

    const [cutscores, isCutscoresLoadingInProgress] = useLoading(fetchCutscores, {});
    const [goals, isGoalsLoadingInProgress] = useLoading(fetchGoals, {});
    const [ZoG, isZoGLoadingInProgress] = useLoading(fetchZoG, {});
    const fetchResults = useCallback(
        async prevResults => {
            if (prevResults[selectedClassId]) {
                return prevResults;
            }
            return {
                ...prevResults,
                [selectedClassId]: await getResults(
                    applications,
                    classesStudents[selectedClassId].students,
                    fourthPeriodAvailability,
                ),
            };
        },
        [applications, classesStudents, selectedClassId, fourthPeriodAvailability],
    );

    const [resultsFromStore, isResultsLoadingInProgress] = useLoading(fetchResults, {});

    const language = getLanguage(selectedAppGroup);

    const selectedApp = selectedAppGroup === READING ? DIBELS8 : IDEL2;

    const denyGatingRulesConfig = isGatingRulesRegulationEnabled ? gatingRulesConfig : {};

    const { allFormattedResults, selectedClassData } = useMemo(
        () =>
            prepareResultsClassData(
                classesStudents[selectedClassId],
                resultsFromStore[selectedClassId],
                language,
                denyGatingRulesConfig,
            ),
        [classesStudents, selectedClassId, resultsFromStore, language, denyGatingRulesConfig],
    );
    const selectedClassExtendedData = useMemo(
        () =>
            selectedAppGroup === READING && Object.keys(applications).includes(BURST)
                ? prepareBurstClassData(selectedClassData, appBMWindows, currentBMWindow.period)
                : selectedClassData,
        [selectedAppGroup, applications, selectedClassData, appBMWindows, currentBMWindow.period],
    );

    const isLoading =
        isFormsLoadingInProgress ||
        isResultsLoadingInProgress ||
        isCutscoresLoadingInProgress ||
        isGoalsLoadingInProgress ||
        isZoGLoadingInProgress;

    const cutscoresWithCLPL = {
        [DIBELS8]: { ...cutscores[DIBELS8] },
        [IDEL2]: mergeWith(cutscores[CLPL], cutscores[IDEL2]),
    };

    return (
        <AppContext.Provider
            value={{
                staff,
                bmWindow: currentBMWindow,
                nextBMWindow,
                checkAndGetSessionStatus,
                reloadPage,
                isUnsyncedData,
                isDemoClassSelected,
                forms,
                formsMap,
                classId: selectedClassId,
                allResults: allFormattedResults.results,
                selectedClassData: selectedClassExtendedData,
                applicationSpecificCallbacks,
                cutscores: cutscoresWithCLPL[selectedApp],
                studentsGoals: goals[selectedApp],
                studentsZoG: ZoG[selectedApp],
                bmWindowConfig,
                selectedAppGroup,
                gatingRulesConfig,
                selectedAssessmentType,
                language,
                fourthPeriodAvailability,
            }}
        >
            {isLoading && <Loading isTransparent />}
            {children}
        </AppContext.Provider>
    );
};

DashboardDataProvider.propTypes = {
    applications: applicationsType.isRequired,
    staff: staffType.isRequired,
    forms: formsType.isRequired,
    classesStudents: classeStudentsType.isRequired,
    applicationSpecificCallbacks: PropTypes.objectOf(PropTypes.shape({})),
    isDemoClassSelected: PropTypes.bool,
    reloadPage: PropTypes.func.isRequired,
    checkAndGetSessionStatus: PropTypes.func.isRequired,
    isUnsyncedData: PropTypes.bool,
    selectedClassId: PropTypes.string.isRequired,
    selectedAppGroup: appGroupType.isRequired,
    bmWindowConfig: PropTypes.string,
    selectedAssessmentType: PropTypes.string,
    children: PropTypes.node.isRequired,
    appBMWindows: PropTypes.arrayOf(bmWindowType).isRequired,
    currentBMWindow: bmWindowType.isRequired,
    nextBMWindow: bmWindowType,
    gatingRulesConfig: gatingRulesConfigType,
    fourthPeriodAvailability: PropTypes.object,
};
DashboardDataProvider.defaultProps = {
    applicationSpecificCallbacks: {},
    isUnsyncedData: false,
    isDemoClassSelected: true,
    bmWindowConfig: '',
    nextBMWindow: undefined,
    gatingRulesConfig: {},
    selectedAssessmentType: '',
    fourthPeriodAvailability: {},
};

export default DashboardDataProvider;
